import { useState } from "react"
import { useGetSellerLeadById } from "@/hooks/admin/useGetSellerLeadById"
import { useGetSellerLeadContact } from "@/hooks/admin/useGetSellerLeadContact"
import { useUpdateSellerLeadStatus } from "@/hooks/admin/useUpdateSellerLeadStatus"
import { useAssignSellerLead } from "@/hooks/admin/useAssignSellerLead"
import { useAddSellerLeadNote } from "@/hooks/admin/useAddSellerLeadNote"
import { useGetAdmins } from "@/hooks/admin/useGetAdmins"
import { useNavigate } from "@tanstack/react-router"
import {
  ChevronLeft,
  CheckCircle2,
  AlertCircle,
  Calendar,
  Store,
  User,
  Loader2,
  Send,
  Activity,
  UserPlus,
  FileText,
  X,
  Package,
  MoreVertical,
  Phone,
  PhoneCall,
  Mail,
  Globe,
  ExternalLink,
  Building2,
  UserCheck,
  MapPin,
} from "lucide-react"

function formatDate(isoStr?: string) {
  if (!isoStr) return "N/A"
  return new Date(isoStr).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  })
}

function SellerLeadContactInline({
  data,
  isLoading,
  isError,
  error,
  onClose,
}: {
  data: any
  isLoading: boolean
  isError: boolean
  error: any
  onClose: () => void
}) {
  if (isLoading) {
    return (
      <div className="rounded-2xl border border-[#be001e26] bg-white p-5 shadow-sm space-y-2 animate-in fade-in duration-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs font-bold text-slate-700">
            <Loader2 className="size-4 animate-spin text-[#be001e]" />
            <span>Fetching seller contact information...</span>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 cursor-pointer">
            <X className="size-4" />
          </button>
        </div>
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-xs text-red-700 space-y-2 animate-in fade-in duration-200">
        <div className="flex items-center justify-between font-bold">
          <span className="flex items-center gap-1.5">
            <AlertCircle className="size-4 text-red-600" /> Unable to load contact details
          </span>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 cursor-pointer">
            <X className="size-4" />
          </button>
        </div>
        <p className="text-[11px] text-red-600">{(error as Error)?.message || "No contact record found for this lead."}</p>
      </div>
    )
  }

  const contact = data.contact || {}
  const companyName = contact.companyName || data.companyName
  const contactPerson = contact.contactPerson
  const emails: string[] = (contact.emails || []).filter((e: any) => typeof e === "string" && e.trim() !== "")
  const phones: string[] = (contact.phones || []).filter((p: any) => typeof p === "string" && p.trim() !== "")
  const website = contact.website || data.website

  const hasContent = companyName || contactPerson || emails.length > 0 || phones.length > 0 || website

  return (
    <div className="rounded-2xl border border-[#be001e26] bg-white p-5 shadow-md space-y-4 animate-in fade-in slide-in-from-top-2 duration-200 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#be001e] to-[#e6202f]" />

      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-100 pb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-[#be001e]/10 text-[#be001e]">
            <PhoneCall className="size-4" />
          </div>
          <h4 className="text-xs font-black uppercase tracking-wider text-slate-800 font-sans">
            Seller Contact Information
          </h4>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700 transition cursor-pointer p-1 rounded-lg hover:bg-slate-100">
          <X className="size-4" />
        </button>
      </div>

      {!hasContent ? (
        <p className="text-xs text-slate-500 italic font-medium">No contact details available for this seller.</p>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Company Name */}
            {companyName && (
              <div className="space-y-1">
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block font-sans">Company Name</span>
                <p className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                  <span>{companyName}</span>
                </p>
              </div>
            )}

            {/* Contact Person */}
            {contactPerson && (
              <div className="space-y-1">
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block font-sans">Contact Person</span>
                <p className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                  <span>{contactPerson}</span>
                </p>
              </div>
            )}

            {/* Phone Numbers */}
            {phones.length > 0 && (
              <div className="space-y-1">
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block font-sans">Phone Numbers</span>
                <div className="flex flex-wrap gap-1.5">
                  {phones.map((phone, idx) => (
                    <a
                      key={idx}
                      href={`tel:${phone}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-800 border border-emerald-200/60 text-[11px] font-bold hover:bg-emerald-100 transition"
                    >
                      <span>{phone}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Email Addresses */}
            {emails.length > 0 && (
              <div className="space-y-1">
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block font-sans">Emails</span>
                <div className="flex flex-wrap gap-1.5">
                  {emails.map((email, idx) => (
                    <a
                      key={idx}
                      href={`mailto:${email}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-blue-50 text-blue-800 border border-blue-200/60 text-[11px] font-bold hover:bg-blue-100 transition"
                    >
                      <span>{email}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Website */}
            {website && (
              <div className="space-y-1 md:col-span-2">
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block font-sans">Official Website</span>
                <a
                  href={website.startsWith("http") ? website : `https://${website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-[#be001e] hover:underline"
                >
                  <span>{website}</span>
                  <ExternalLink className="size-3 shrink-0" />
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function SellerLeadCompanyOverviewCard({ lead }: { lead: any }) {
  const [isWorkingDaysExpanded, setIsWorkingDaysExpanded] = useState(false)
  const seller = lead?.externalSeller || {}

  const hasContent = lead.status || seller.annualTurnover || seller.businessType || lead.productName || seller.establishment || seller.employeeCount || seller.workingDays

  if (!hasContent) return null

  return (
    <div className="space-y-3">
      <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5 font-sans">
        <Store className="size-4 text-[#e6202f]" /> Company Overview
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 bg-slate-50/50 border border-slate-200/60 rounded-xl p-4 text-xs font-semibold text-slate-700 font-sans">
        {lead.status && (
          <div className="space-y-1">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block">Status</span>
            <span className="text-slate-800 font-bold leading-relaxed block">{lead.status}</span>
          </div>
        )}
        {lead.productName && (
          <div className="space-y-1">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block">Product Name</span>
            <span className="text-slate-800 font-bold leading-relaxed block">{lead.productName}</span>
          </div>
        )}
        {seller.businessType && (
          <div className="space-y-1">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block">Business Type</span>
            <span className="text-slate-800 font-bold leading-relaxed block">{seller.businessType}</span>
          </div>
        )}
        {seller.annualTurnover && (
          <div className="space-y-1">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block">Annual Turnover</span>
            <span className="text-slate-800 font-bold leading-relaxed block">{seller.annualTurnover}</span>
          </div>
        )}
        {seller.establishment && (
          <div className="space-y-1">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block">Established</span>
            <span className="text-slate-800 font-bold leading-relaxed block">{seller.establishment}</span>
          </div>
        )}
        {seller.employeeCount && (
          <div className="space-y-1">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block">Employees</span>
            <span className="text-slate-800 font-bold leading-relaxed block">{seller.employeeCount}</span>
          </div>
        )}
        {seller.workingDays && (
          <div className="space-y-1 md:col-span-2">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block">Working Days</span>
            <span className="text-slate-800 font-bold leading-relaxed block">
              {isWorkingDaysExpanded || seller.workingDays.length <= 40
                ? seller.workingDays
                : `${seller.workingDays.slice(0, 40)}...`}
              {seller.workingDays.length > 40 && (
                <button
                  type="button"
                  onClick={() => setIsWorkingDaysExpanded(!isWorkingDaysExpanded)}
                  className="ml-1 text-[#be001e] hover:underline text-[11px] font-extrabold cursor-pointer inline-block"
                >
                  {isWorkingDaysExpanded ? "show less" : "more"}
                </button>
              )}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

function SellerLeadLocationCard({ lead }: { lead: any }) {
  const seller = lead?.externalSeller || {}
  const hasContent = seller.locality || seller.region || seller.country || seller.streetAddress || seller.companyUrl

  if (!hasContent) return null

  return (
    <div className="space-y-3">
      <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5 font-sans">
        <MapPin className="size-4 text-[#e6202f]" /> Location & Online Presence
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 bg-slate-50/50 border border-slate-200/60 rounded-xl p-4 text-xs font-semibold text-slate-700 font-sans">
        {seller.locality && (
          <div className="space-y-1">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block">Locality</span>
            <span className="text-slate-800 font-bold leading-relaxed block">{seller.locality}</span>
          </div>
        )}
        {seller.region && (
          <div className="space-y-1">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block">Region / State</span>
            <span className="text-slate-800 font-bold leading-relaxed block">{seller.region}</span>
          </div>
        )}
        {seller.country && (
          <div className="space-y-1">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block">Country</span>
            <span className="text-slate-800 font-bold leading-relaxed block">{seller.country.toUpperCase()}</span>
          </div>
        )}
        {seller.companyUrl && (
          <div className="space-y-1">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block">Company Website</span>
            <span className="text-slate-800 hover:text-[#be001e] font-bold leading-relaxed hover:underline break-all block">
              <a href={seller.companyUrl} target="_blank" rel="noopener noreferrer">
                {seller.companyUrl}
              </a>
            </span>
          </div>
        )}
        {seller.streetAddress && (
          <div className="space-y-1 md:col-span-2 border-t border-slate-100/80 pt-2.5">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block">Street Address</span>
            <span className="text-slate-800 font-bold leading-relaxed block">{seller.streetAddress}</span>
          </div>
        )}
      </div>
    </div>
  )
}

function SellerLeadManagementCard({ lead }: { lead: any }) {
  const [isDescExpanded, setIsDescExpanded] = useState(false)
  const seller = lead?.externalSeller || {}
  const hasContent = lead.assignedTo?.name || seller.description

  if (!hasContent) return null

  return (
    <div className="space-y-3">
      <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5 font-sans">
        <UserCheck className="size-4 text-[#e6202f]" /> Management & Description
      </h4>
      <div className="space-y-3 bg-slate-50/50 border border-slate-200/60 rounded-xl p-4 text-xs font-semibold text-slate-700 font-sans">
        {lead.assignedTo?.name && (
          <div className="space-y-1">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block">Assigned Admin</span>
            <span className="text-[#be001e] font-bold leading-relaxed flex items-center gap-1">
              <User className="size-3" /> {lead.assignedTo.name}
            </span>
          </div>
        )}
        {seller.description && (
          <div className="space-y-1 pt-2 border-t border-slate-100">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block">Company Description</span>
            <span className="text-slate-600 font-medium leading-relaxed whitespace-pre-line inline">
              {seller.description.length > 150 && !isDescExpanded
                ? `${seller.description.slice(0, 150)}...`
                : seller.description}
            </span>
            {seller.description.length > 150 && (
              <button
                type="button"
                onClick={() => setIsDescExpanded(!isDescExpanded)}
                className="text-[11px] text-[#be001e] font-bold hover:underline ml-1.5 cursor-pointer inline-block"
              >
                {isDescExpanded ? "show less" : "more"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function SellerLeadDetailView({ leadId, onBack }: { leadId: string; onBack: () => void }) {
  const { data: lead, isLoading, isError, error } = useGetSellerLeadById(leadId)
  const { mutate: updateStatus, isPending: updatingStatus } = useUpdateSellerLeadStatus()
  const { mutate: assignLead, isPending: assigningLead } = useAssignSellerLead()
  const { mutate: addNote, isPending: addingNote } = useAddSellerLeadNote()
  const { data: adminsList = [], isLoading: isLoadingAdmins } = useGetAdmins()

  const [successToast, setSuccessToast] = useState<string | null>(null)
  const [activeForm, setActiveForm] = useState<"status" | "assign" | "notes" | null>(null)
  const [showMenu, setShowMenu] = useState(false)
  const [showContact, setShowContact] = useState(false)

  const sellerTargetId = lead?.externalSellerId || lead?.externalSeller?.id || lead?.id || leadId
  const {
    data: contactData,
    isLoading: isLoadingContact,
    isError: isContactError,
    error: contactError,
  } = useGetSellerLeadContact(sellerTargetId, showContact)

  const [statusVal, setStatusVal] = useState("CLOSED")
  const [statusMessage, setStatusMessage] = useState("")
  const [adminUserId, setAdminUserId] = useState("")
  const [noteMessage, setNoteMessage] = useState("")

  const triggerToast = (msg: string) => {
    setSuccessToast(msg)
    setTimeout(() => setSuccessToast(null), 3000)
  }

  const handleUpdateStatus = (e: React.FormEvent) => {
    e.preventDefault()
    updateStatus({ id: leadId, payload: { status: statusVal, message: statusMessage } }, {
      onSuccess: () => {
        triggerToast("Status updated successfully")
        setActiveForm(null)
      }
    })
  }

  const handleAssign = (e: React.FormEvent) => {
    e.preventDefault()
    assignLead({ id: leadId, payload: { adminUserId } }, {
      onSuccess: () => {
        triggerToast("Admin assigned successfully")
        setActiveForm(null)
      }
    })
  }

  const handleAddNote = (e: React.FormEvent) => {
    e.preventDefault()
    addNote({ id: leadId, payload: { message: noteMessage } }, {
      onSuccess: () => {
        triggerToast("Note added successfully")
        setActiveForm(null)
        setNoteMessage("")
      }
    })
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 animate-in fade-in">
        <Loader2 className="size-8 animate-spin text-[#be001e]" />
        <p className="mt-4 text-sm font-bold text-slate-500 font-sans tracking-wide uppercase">
          Loading Lead Details...
        </p>
      </div>
    )
  }

  if (isError || !lead) {
    return (
      <div className="max-w-4xl mx-auto space-y-4 font-sans text-left">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-800 transition-colors text-sm font-bold cursor-pointer"
        >
          <ChevronLeft className="size-4" /> Back to List
        </button>
        <div className="flex items-center gap-3 rounded-2xl border border-red-100 bg-red-50 p-5 text-sm text-[#e6202f]">
          <AlertCircle className="size-5 shrink-0" />
          <p className="font-semibold">{(error as Error)?.message || "Failed to load lead details."}</p>
        </div>
      </div>
    )
  }

  const rfq = lead.rfqTarget?.rfq
  const buyerUser = rfq?.buyer?.user

  return (
    <div className="animate-in fade-in duration-200 text-left font-sans max-w-6xl mx-auto pb-20 relative">
      {successToast && (
        <div className="fixed top-20 right-6 bg-emerald-600 text-white text-xs font-bold px-4 py-2.5 rounded-lg shadow-lg flex items-center gap-2 z-[99] animate-in fade-in slide-in-from-top-4 duration-300">
          <CheckCircle2 className="size-4 shrink-0" />
          <span>{successToast}</span>
        </div>
      )}

      <div className="group relative overflow-hidden bg-white border border-[#be001e26] hover:border-[#be001e4d] rounded-2xl p-5 space-y-4 shadow-sm hover:shadow-[0_20px_50px_rgba(230,32,47,0.06)] transition-all duration-300">
        <div className="absolute top-0 left-0 right-0 h-[1px] group-hover:h-[3.5px] bg-gradient-to-r from-[#be001e] to-[#e6202f] rounded-t-2xl opacity-0 group-hover:opacity-100 transition-all duration-300" />

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="flex items-center justify-center p-2 rounded-lg border border-[#be001e26] bg-white hover:bg-[#be001e0d] hover:text-[#be001e] hover:shadow-sm transition-all cursor-pointer text-slate-800 shrink-0"
              title="Back to External Sellers"
            >
              <ChevronLeft className="size-4" />
            </button>
            <div>
              <h3 className="text-lg font-black text-slate-800 font-sans leading-tight">
                {lead?.externalSeller?.companyName}
              </h3>
              <div className="flex items-center gap-2 mt-1 text-xs text-slate-500 font-medium">
                <Calendar className="size-3.5" />
                Created {formatDate(lead.createdAt)}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 w-full md:w-auto shrink-0">
            <button
              type="button"
              onClick={() => setShowContact((prev) => !prev)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer font-sans border shadow-sm ${
                showContact
                  ? "bg-[#be001e] border-[#8a0016] text-white shadow-inner"
                  : "bg-[#be001e] hover:bg-[#e6202f] border-[#be001e] text-white"
              }`}
            >
              <Phone className="size-4"></Phone>
              <span>Contact</span>
            </button>

            <div className="relative">
              <button
                type="button"
                onClick={() => setShowMenu((prev) => !prev)}
                className="flex items-center justify-center p-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300 text-slate-700 shadow-sm transition-all cursor-pointer"
                title="Actions"
              >
                <MoreVertical className="size-4 text-slate-600" />
              </button>

              {showMenu && (
                <>
                  {/* Backdrop to close menu on click outside */}
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowMenu(false)}
                  />

                  <div className="absolute right-0 mt-2 w-44 rounded-xl bg-white border border-slate-200 shadow-lg p-1.5 z-20 animate-in fade-in zoom-in-95 duration-150 space-y-0.5">
                    <button
                      type="button"
                      onClick={() => {
                        setActiveForm(activeForm === "status" ? null : "status")
                        setShowMenu(false)
                      }}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-lg transition-colors cursor-pointer text-left ${
                        activeForm === "status"
                          ? "bg-[#be001e]/10 text-[#be001e]"
                          : "text-slate-700 hover:bg-slate-100"
                      }`}
                    >
                      <Activity className="size-4 text-[#be001e]" />
                      <span>Update Status</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setActiveForm(activeForm === "assign" ? null : "assign")
                        setShowMenu(false)
                      }}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-lg transition-colors cursor-pointer text-left ${
                        activeForm === "assign"
                          ? "bg-[#be001e]/10 text-[#be001e]"
                          : "text-slate-700 hover:bg-slate-100"
                      }`}
                    >
                      <UserPlus className="size-4 text-[#be001e]" />
                      <span>Assign</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setActiveForm(activeForm === "notes" ? null : "notes")
                        setShowMenu(false)
                      }}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-lg transition-colors cursor-pointer text-left ${
                        activeForm === "notes"
                          ? "bg-[#be001e]/10 text-[#be001e]"
                          : "text-slate-700 hover:bg-slate-100"
                      }`}
                    >
                      <FileText className="size-4 text-[#be001e]" />
                      <span>Add Note</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {activeForm && (
          <div className="border border-[#be001e26] p-3 md:p-4 rounded-2xl bg-[#be001e02] shadow-sm animate-in slide-in-from-top-2 duration-200">
            {activeForm === "status" && (
              <form onSubmit={handleUpdateStatus} className="space-y-3 w-full">
                <div className="flex justify-between items-center border-b border-[#be001e1a] pb-2">
                  <h3 className="text-xs font-black text-slate-800 uppercase tracking-wider flex items-center gap-1.5 font-sans">
                    <Activity className="size-4 text-[#e6202f]" /> Update Lead Status
                  </h3>
                  <button type="button" onClick={() => setActiveForm(null)} className="text-slate-400 hover:text-slate-700 cursor-pointer">
                    <X className="size-4" />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 font-sans">New Status</label>
                    <select
                      value={statusVal}
                      onChange={(e) => setStatusVal(e.target.value)}
                      className="w-full bg-white border border-slate-200 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#be001e] cursor-pointer font-sans"
                    >
                      <option value="NEW">NEW</option>
                      <option value="ASSIGNED">ASSIGNED</option>
                      <option value="CONTACTED">CONTACTED</option>
                      <option value="IN_CONVERSATION">IN_CONVERSATION</option>
                      <option value="ONBOARDED">ONBOARDED</option>
                      <option value="REJECTED">REJECTED</option>
                      <option value="CLOSED">CLOSED</option>
                    </select>
                  </div>
                  <div className="space-y-1 md:col-span-2">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 font-sans">Message / Notes</label>
                    <textarea
                      value={statusMessage}
                      onChange={(e) => setStatusMessage(e.target.value)}
                      rows={2}
                      placeholder="E.g., Spoke to the seller, they are interested..."
                      className="w-full bg-white border border-slate-200 p-3 rounded-lg text-xs font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#be001e] font-sans"
                      required
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="submit"
                    disabled={updatingStatus}
                    className="bg-[#be001e] hover:bg-[#e6202f] text-white font-bold text-xs px-5 py-2.5 rounded-lg transition-colors cursor-pointer disabled:opacity-50 font-sans flex items-center gap-2"
                  >
                    {updatingStatus ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
                    Update Status
                  </button>
                </div>
              </form>
            )}

            {activeForm === "assign" && (
              <form onSubmit={handleAssign} className="space-y-3 w-full">
                <div className="flex justify-between items-center border-b border-[#be001e1a] pb-2">
                  <h3 className="text-xs font-black text-slate-800 uppercase tracking-wider flex items-center gap-1.5 font-sans">
                    <UserPlus className="size-4 text-[#e6202f]" /> Assign Admin Onboarding
                  </h3>
                  <button type="button" onClick={() => setActiveForm(null)} className="text-slate-400 hover:text-slate-700 cursor-pointer">
                    <X className="size-4" />
                  </button>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-end gap-3 max-w-xl">
                  <div className="space-y-1 flex-1 min-w-0">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 font-sans block">Select Admin</label>
                    {isLoadingAdmins ? (
                      <p className="text-sm text-slate-500 italic">Loading admins...</p>
                    ) : (
                      <select
                        value={adminUserId}
                        onChange={(e) => setAdminUserId(e.target.value)}
                        className="w-full h-9 bg-white border border-slate-200 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#be001e] cursor-pointer font-sans"
                        required
                      >
                        <option value="">Select an admin...</option>
                        {adminsList.map((admin: any) => (
                          <option key={admin.id} value={admin.id}>
                            {admin.name} ({admin.email})
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div className="flex justify-end sm:justify-start shrink-0">
                    <button
                      type="submit"
                      disabled={assigningLead || !adminUserId}
                      className="h-9 bg-[#be001e] hover:bg-[#e6202f] text-white font-bold text-xs px-5 rounded-lg transition-colors cursor-pointer disabled:opacity-50 font-sans flex items-center justify-center gap-2 shadow-sm"
                    >
                      {assigningLead ? <Loader2 className="size-4 animate-spin" /> : <CheckCircle2 className="size-4" />}
                      Assign
                    </button>
                  </div>
                </div>
              </form>
            )}

            {activeForm === "notes" && (
              <form onSubmit={handleAddNote} className="space-y-3 w-full">
                <div className="flex justify-between items-center border-b border-[#be001e1a] pb-2">
                  <h3 className="text-xs font-black text-slate-800 uppercase tracking-wider flex items-center gap-1.5 font-sans">
                    <FileText className="size-4 text-[#e6202f]" /> Add Internal Note
                  </h3>
                  <button type="button" onClick={() => setActiveForm(null)} className="text-slate-400 hover:text-slate-700 cursor-pointer">
                    <X className="size-4" />
                  </button>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 font-sans">Note Content</label>
                  <textarea
                    value={noteMessage}
                    onChange={(e) => setNoteMessage(e.target.value)}
                    rows={3}
                    placeholder="Add an internal observation or update..."
                    className="w-full bg-white border border-slate-200 p-3 rounded-lg text-xs font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#be001e] font-sans"
                    required
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="submit"
                    disabled={addingNote || !noteMessage.trim()}
                    className="bg-[#be001e] hover:bg-[#e6202f] text-white font-bold text-xs px-5 py-2.5 rounded-lg transition-colors cursor-pointer disabled:opacity-50 font-sans flex items-center gap-2"
                  >
                    {addingNote ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
                    Save Note
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {showContact && (
          <SellerLeadContactInline
            data={contactData}
            isLoading={isLoadingContact}
            isError={isContactError}
            error={contactError}
            onClose={() => setShowContact(false)}
          />
        )}

        <div className="h-px bg-[#be001e1a] w-full" />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <SellerLeadCompanyOverviewCard lead={lead} />
            <SellerLeadLocationCard lead={lead} />
            <SellerLeadManagementCard lead={lead} />

            <div className="space-y-4">
              <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5 font-sans">
                <Package className="size-4 text-[#e6202f]" /> Associated RFQ & Buyer
              </h4>
              {rfq ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 bg-slate-50/50 border border-slate-200/60 rounded-xl p-4 text-xs font-semibold text-slate-700 font-sans">
                  {buyerUser?.name && (
                    <div className="space-y-1">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block">Buyer Name</span>
                      <span className="text-slate-800 font-bold leading-relaxed block">{buyerUser.name}</span>
                    </div>
                  )}
                  {buyerUser?.email && (
                    <div className="space-y-1">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block">Buyer Email</span>
                      <span className="text-slate-800 font-bold leading-relaxed block">{buyerUser.email}</span>
                    </div>
                  )}
                  {rfq.status && (
                    <div className="space-y-1">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block">RFQ Status</span>
                      <span className="text-slate-800 font-bold leading-relaxed block">{rfq.status}</span>
                    </div>
                  )}
                  {lead.rfqTarget?.quantity && (
                    <div className="space-y-1">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block">Quantity Requested</span>
                      <span className="text-slate-800 font-bold leading-relaxed block">{lead.rfqTarget.quantity}</span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-slate-400 italic bg-slate-50/50 p-4 rounded-xl border border-slate-100 font-sans">
                  No associated RFQ found for this lead.
                </p>
              )}
            </div>
          </div>

          <div className="lg:col-span-1 border-t lg:border-t-0 lg:border-l border-slate-100 pt-4 lg:pt-0 lg:pl-6">
            <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5 font-sans mb-3">
              <Activity className="size-4 text-[#e6202f]" /> Activity Log
            </h4>
            
            <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2 pb-4">
              {lead.activityLogs && lead.activityLogs.length > 0 ? (
                lead.activityLogs.map((log: any, idx: number) => {
                  const isLast = idx === lead.activityLogs.length - 1
                  return (
                    <div key={log.id} className="relative pl-5 font-sans">
                      {!isLast && (
                        <div className="absolute left-[7px] top-5 bottom-[-16px] w-[2px] bg-slate-100" />
                      )}
                      
                      <div className="absolute left-0 top-1 w-4 h-4 rounded-full bg-white border-[1.5px] border-[#be001e26] flex items-center justify-center z-10 shadow-sm">
                        <div className="w-1 h-1 rounded-full bg-[#be001e]" />
                      </div>

                      <div className="bg-white border border-[#be001e1a] rounded-xl p-2.5 shadow-sm hover:shadow-md hover:border-[#be001e33] transition-all group">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                          <span className="text-[11px] font-black text-slate-800 uppercase tracking-wider group-hover:text-[#be001e] transition-colors">
                            {log.action.replace(/_/g, " ")}
                          </span>
                          <span className="text-[9px] font-bold text-slate-400">
                            {formatDate(log.createdAt)}
                          </span>
                        </div>
                        <p className="text-xs text-slate-600 font-medium leading-relaxed">
                          {log.message || "No message provided."}
                        </p>
                        
                        {(log.toStatus || log.user) && (
                          <div className="mt-2.5 pt-2 border-t border-slate-100 flex flex-wrap items-center gap-3">
                            {log.toStatus && (
                              <span className="inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-black uppercase text-slate-600 border border-slate-200">
                                <Activity className="size-2.5" /> {log.toStatus}
                              </span>
                            )}
                            {log.user && (
                              <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase text-slate-500">
                                <User className="size-2.5" /> By {log.user.name}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })
              ) : (
                <div className="text-center py-10 bg-slate-50/50 rounded-xl border border-slate-100 font-sans">
                  <Activity className="size-6 text-slate-300 mx-auto mb-2" />
                  <p className="text-xs font-semibold text-slate-400">No activity logs yet.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export function SellerLeadDetailsPage({ leadId }: { leadId: string }) {
  const navigate = useNavigate()

  const handleGoBack = () => {
    navigate({ to: "/external-sellers" })
  }

  return (
    <SellerLeadDetailView leadId={leadId} onBack={handleGoBack} />
  )
}
